import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, RotateCcw } from "lucide-react";

const TIMINGS = [2, 4, 12, 24, 48, 72];
const TAGS = ["first_name", "day_date", "time", "business_contact_number", "service", "date", "cleaners", "duration", "price", "business", "business_phone"];

export default function SmsReminderSettings({ overview, onSaved }) {
  const [enabled, setEnabled] = useState(overview.sms_enabled);
  const [skipLate, setSkipLate] = useState(overview.reminder_skip_late);
  const [hours, setHours] = useState(overview.reminder_hours);
  const [template, setTemplate] = useState(overview.sms_template);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/sms/preview", { template }).then((r) => setPreview(r.data)).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [template]);

  const insertTag = (tag) => {
    const el = ref.current;
    const pos = el ? el.selectionStart : template.length;
    setTemplate(template.slice(0, pos) + `{${tag}}` + template.slice(pos));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/sms", { sms_enabled: enabled, sms_template: template, reminder_hours: Number(hours), reminder_skip_late: skipLate });
      toast.success("SMS reminder settings saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="sms-reminder-settings">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 font-medium"><MessageSquare className="w-4 h-4 text-brand" /> Automatic reminders</div>
          <p className="text-sm text-muted-foreground mt-1">Text each client exactly {hours} hours before every Scheduled booking (incl. walk-throughs). Cancelled, paused or moved bookings are never reminded for the old time.</p>
          {!overview.sms_live && enabled && <p className="text-xs text-[hsl(272_80%_45%)] mt-1" data-testid="sms-reminders-test-note">Live sending is off, so reminders are only logged (Test mode).</p>}
        </div>
        <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="sms-enabled-switch" />
      </div>

      <div className="space-y-2 mt-8 max-w-xs">
        <Label className="label-caps text-muted-foreground">Reminder timing</Label>
        <select value={hours} onChange={(e) => setHours(e.target.value)} data-testid="sms-timing-select"
          className="w-full border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand">
          {TIMINGS.map((h) => <option key={h} value={h}>{h} hours before</option>)}
        </select>
      </div>

      <div className="flex items-center justify-between gap-4 mt-6 max-w-xl">
        <div>
          <div className="text-sm font-medium">Skip bookings made less than {hours} hours ahead</div>
          <p className="text-xs text-muted-foreground">Also applies when a booking is moved inside the window — they already get the change text.</p>
        </div>
        <Switch checked={skipLate} onCheckedChange={setSkipLate} data-testid="sms-skip-late-switch" />
      </div>

      <div className="space-y-2 mt-8">
        <div className="flex items-center justify-between">
          <Label className="label-caps text-muted-foreground">Message template</Label>
          <button type="button" onClick={() => setTemplate(overview.default_template)} data-testid="sms-template-reset"
            className="text-xs text-muted-foreground hover:text-brand inline-flex items-center gap-1 transition-colors">
            <RotateCcw className="w-3 h-3" /> Reset to default
          </button>
        </div>
        <Textarea ref={ref} value={template} onChange={(e) => setTemplate(e.target.value)} rows={5} data-testid="sms-template-input" />
        <div className="flex flex-wrap gap-2 pt-1">
          {TAGS.map((t) => (
            <button key={t} type="button" onClick={() => insertTag(t)} data-testid={`sms-tag-${t}`}
              className="text-xs font-mono border border-border/70 rounded-full px-2.5 py-1 hover:border-brand hover:text-brand transition-colors">
              {`{${t}}`}
            </button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{"{price}"} includes 10% GST. Dates and times use Australia/Brisbane.</p>
      </div>

      {preview && (
        <div className="mt-6 rounded-sm bg-gradient-to-br from-brand/10 to-[hsl(272_90%_50%)]/10 border border-brand/30 p-5" data-testid="sms-preview">
          <div className="label-caps text-muted-foreground mb-2">Preview{preview.sample_client ? ` · ${preview.sample_client}` : ""}</div>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="sms-preview-body">{preview.body}</p>
          <div className="text-xs text-muted-foreground mt-3" data-testid="sms-preview-count">
            {preview.chars} characters · {preview.segments} SMS segment{preview.segments > 1 ? "s" : ""}
          </div>
        </div>
      )}

      <Button onClick={save} disabled={saving} data-testid="save-sms-button" className="rounded-sm mt-8">
        {saving ? "Saving…" : "Save SMS Settings"}
      </Button>
    </section>
  );
}
